import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

import { IconButton, MenuItem, Tooltip } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Copy, EllipsisVertical, Loader2, Trash2, X } from 'lucide-react';

import { createTanstackTable, toastPromise } from '../../../common/utils';
import { useGetProjectById } from '../../../hooks';
import {
  useGetSharedModels,
  useImportSharedModel,
  useRevokeSharedModel,
} from '../../../hooks/tanstack/useSharedModelsHook';
import {
  useGetRouteParams,
  useGetSearchResults,
} from '../../../hooks/tanstack/useUtilsHook';
import { type Author } from '../../../types';
import ConfirmDeleteDialog from '../../ConfirmDeleteDialog';
import MenuDropdown from '../../MenuDropdown';
import SearchInput from '../../SearchInput';
import TanstackTable from '../../TanstackTable';
import WarningDialog from '../../WarningDialog';

export type DataRow = {
  id: number;
  name: string;
  description: string;
  revoke: {
    by?: Author;
    at?: string;
  };
  sender: {
    email: string;
    full_name: string;
  };
  shared_from: {
    id: number;
    name: string;
  };
  receivers: {
    email: string;
  }[];
  createdAt: string;
};

const columnHelper = createColumnHelper<DataRow>();

const columns = ({ handleSelect }: { handleSelect: (id: number) => void }) => [
  columnHelper.accessor('name', {
    cell: (info) => (
      <div className="group flex items-center gap-2 text-start">
        <p className="w-full">{info.getValue()}</p>
      </div>
    ),
    header: () => (
      <div className="flex items-start w-[70px] gap-2">
        <p>Model</p>
      </div>
    ),
  }),
  columnHelper.accessor('description', {
    cell: (info) => (
      <div className="group flex items-center gap-2 line-clamp-2 text-start">
        <p className="w-full">{info.getValue()}</p>
      </div>
    ),
    header: () => (
      <div className="flex items-start gap-2">
        <p>Description</p>
      </div>
    ),
  }),
  columnHelper.accessor('shared_from', {
    id: 'shared_from',
    cell: (info) => {
      const sharedFrom = info.getValue();

      const { orgId, projectId } = useGetRouteParams();

      const { data: project } = useGetProjectById(
        {
          projectId,
          orgId,
        },
        {
          enabled: !!orgId && !!projectId,
        }
      );

      const navigate = useNavigate();

      const handleNavigate = (event: React.MouseEvent) => {
        event.stopPropagation();
        navigate(`${sharedFrom.id}`);
      };

      if (project?.is_guest_project) {
        return null;
      }

      return sharedFrom ? (
        <div className="w-full">
          <span
            onClick={handleNavigate}
            className="underline text-primary-bright-blue font-semibold"
          >
            {sharedFrom.name}
          </span>
        </div>
      ) : (
        <div className="text-gray-400 w-full text-start">--</div>
      );
    },
    header: () => {
      const { orgId, projectId } = useGetRouteParams();

      const { data: project } = useGetProjectById(
        {
          projectId,
          orgId,
        },
        {
          enabled: !!orgId && !!projectId,
        }
      );

      if (project?.is_guest_project) {
        return null;
      }

      return <div>Based on</div>;
    },
  }),
  columnHelper.accessor('sender', {
    id: 'sender',
    cell: (info) =>
      info.getValue() ? (
        <div className="w-full">{info.getValue().full_name}</div>
      ) : (
        <div className="text-gray-400 w-full text-start">--</div>
      ),
    header: () => <div>Shared By</div>,
  }),
  columnHelper.accessor('receivers', {
    id: 'receivers',
    cell: (info) => {
      const receivers = info.getValue();
      const revoke = info.row.original.revoke;

      const isRevoked = !!revoke.by;

      return receivers ? (
        <div className="w-full flex flex-col">
          <p>{receivers.map((i) => i.email).join(', ')}</p>
          {isRevoked && (
            <Tooltip
              title={`Revoked by ${revoke.by?.full_name} on ${revoke.at ? format(revoke.at, 'y/MM/dd') : ''}`}
            >
              <p className="text-sm rounded-full w-fit font-semibold bg-[#FFECE8] text-[#B8341B] px-2 py-1">
                Revoked
              </p>
            </Tooltip>
          )}
        </div>
      ) : (
        <div className="text-gray-400 w-full text-center">--</div>
      );
    },
    header: () => <div>Shared To</div>,
  }),
  columnHelper.accessor('createdAt', {
    id: 'createdAt',
    cell: (info) => {
      const value = info.getValue();
      const id = info.row.original.id;
      const name = info.row.original.name;
      const description = info.row.original.description;
      const updatedAt = info.row.original.createdAt;
      const isRevoked = !!info.row.original.revoke.by;

      const { projectId, orgId } = useGetRouteParams();

      const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

      const [openRevokeAccessDialog, setOpenRevokeAccessDialog] =
        useState(false);

      const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] =
        useState(false);

      const open = Boolean(anchorEl);

      const {
        mutateAsync: importSharedModel,
        isPending: isImportingSharedModel,
      } = useImportSharedModel();

      const { mutate: revokeSharedModel, isPending: isRevokingSharedModel } =
        useRevokeSharedModel({
          onSuccess: () => {
            toastPromise({
              promise: Promise.resolve(),
              content: 'Access revoked successfully',
            });
            setOpenRevokeAccessDialog(false);
            handleCloseDropdown();
          },
          onError: () => {
            toastPromise({
              promise: Promise.reject(),
              content: 'Failed to revoke access',
            });
            setOpenRevokeAccessDialog(false);
            handleCloseDropdown();
          },
        });

      const handleImportModel = async () => {
        const res = importSharedModel({
          orgId,
          projectId,
          modelId: id,
        });

        if (res) {
          const data = await res;

          toastPromise({
            promise: res,
            content: (
              <>
                Model imported successfully.{' '}
                <Link
                  to={`/${orgId}/projects/${projectId}/models/${data.id}`}
                  className="underline cursor-pointer text-sm"
                >
                  Click here to go to the model.
                </Link>
              </>
            ),
          });
        }
        handleCloseDropdown();
      };

      const handleCloseDropdown = () => setAnchorEl(null);

      const handleOpenConfirmDeleteDialog = () => {
        setOpenConfirmDeleteDialog(true);
      };

      const handleCloseConfirmDeleteDialog = () => {
        setOpenConfirmDeleteDialog(false);
      };

      return (
        <div className="w-full flex justify-between items-center">
          <p>{value ? format(value as string, 'y/MM/dd h:mm a') : '--'}</p>
          {isImportingSharedModel || isRevokingSharedModel ? (
            <IconButton disabled>
              <Loader2 className="text-primary-bright-blue h-5 w-5 animate-spin" />
            </IconButton>
          ) : (
            <MenuDropdown
              trigger={
                <button>
                  <EllipsisVertical width={20} height={20} />
                </button>
              }
              anchorEl={anchorEl}
              setAnchorEl={setAnchorEl}
              style={{
                marginTop: '5px',
              }}
              open={open}
            >
              <MenuItem
                onClick={(e) => {
                  handleImportModel();
                }}
              >
                <div className="flex items-center space-x-2">
                  <Copy size={15} />
                  <p>Import model</p>
                </div>
              </MenuItem>
              <div className="!border-t !border-[#E4E7EC]" />
              {isRevoked ? (
                <MenuItem
                  onClick={() => {
                    handleOpenConfirmDeleteDialog();
                    handleCloseDropdown();
                  }}
                >
                  <div className="flex items-center space-x-2 text-primary-red">
                    <Trash2 size={15} />
                    <p>Delete model</p>
                  </div>
                </MenuItem>
              ) : (
                <MenuItem
                  onClick={() => {
                    setOpenRevokeAccessDialog(true);
                    handleCloseDropdown();
                  }}
                >
                  <div className="flex items-center space-x-2 text-primary-red">
                    <X size={15} />
                    <p>Revoke access</p>
                  </div>
                </MenuItem>
              )}
            </MenuDropdown>
          )}
          <ConfirmDeleteDialog
            type="sharedModel"
            row={{
              id,
              name,
              description,
              modified: updatedAt,
            }}
            open={openConfirmDeleteDialog}
            handleClose={handleCloseConfirmDeleteDialog}
          />
          <WarningDialog
            open={openRevokeAccessDialog}
            content={undefined}
            isPending={isRevokingSharedModel}
            title="Are you sure you want to revoke this user's access"
            primaryActionText="Yes"
            secondaryActionText="Cancel"
            showIcon={false}
            handleSecondaryAction={() => {
              setOpenRevokeAccessDialog(false);
            }}
            handlePrimaryAction={() => {
              revokeSharedModel({
                orgId,
                projectId,
                modelId: id,
              });
            }}
          />
        </div>
      );
    },
    sortingFn: (a, b) =>
      new Date(a.original.createdAt as string).getTime() -
      new Date(b.original.createdAt as string).getTime(),
    header: () => <div>Date Sent</div>,
  }),
];

const SharedModelsTable = () => {
  const [data, setData] = useState<DataRow[]>([]);

  const { projectId, orgId } = useGetRouteParams();

  const form = useForm();

  const { watch } = form;

  const navigate = useNavigate();

  const { data: sharedModels, isLoading: isLoadingSharedModels } =
    useGetSharedModels(
      {
        projectId,
        orgId,
      },
      {
        enabled: !!projectId && !!orgId,
      }
    );

  const searchText = watch('search');

  const searchResults = useGetSearchResults<DataRow>({
    searchValue: searchText,
    data,
    filterFields: ['name', 'sender.full_name'],
  });

  useEffect(() => {
    if (sharedModels) {
      setData(
        sharedModels.map((r) => ({
          id: r.id,
          name: r.name,
          description: r.description,
          sender: {
            email: r.shared_by?.email,
            full_name: r.shared_by?.full_name,
          },
          revoke: {
            by: r.revoked_by,
            at: r.revoked_at,
          },
          shared_from: r.shared_from,
          receivers: r.shared_with,
          createdAt: r.created_at,
        })) as DataRow[]
      );
    }
  }, [sharedModels]);

  const handleSelect = (id: number) => {
    navigate(`${id}?type=shared`);
  };

  const table = createTanstackTable<DataRow>({
    data: searchResults ?? data,
    columns: columns({ handleSelect }),
    defaultSorting: {
      id: 'createdAt',
      desc: true,
    },
  });

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-bold">Shared</h1>
      <FormProvider {...form}>
        <form className="flex justify-between">
          <div className="flex w-[300px] gap-2">
            <SearchInput />
          </div>
        </form>
      </FormProvider>
      <TanstackTable
        table={table}
        handleSelect={{
          onClick: handleSelect,
          lockColIndex: 5,
        }}
        isLoading={isLoadingSharedModels}
        emptyState={searchText ? 'No results' : 'No shared model available'}
      />
    </div>
  );
};

export default SharedModelsTable;
